import { useEffect, useState } from "react";
import { Button, Form, Input, Modal, Select, Space, Switch, Tag, message } from "antd";
import { AuditOutlined } from "@ant-design/icons";
import axiosInstance from "../../../variables/api/axiosInstance";
import { rightsService } from "../../../services/rightsService";

const { TextArea } = Input;

/**
 * Marquage « en contentieux » d'un sinistre + juridiction + cabinet affecté + résumé.
 * Alimente la colonne Juridiction de la synthèse conseil et l'état « Sinistres en contentieux ».
 * Marquage sous habilitation HSI094.
 */
const ContentieuxButton = ({ sinistreCodeId }) => {
    const [info, setInfo] = useState(null);
    const [open, setOpen] = useState(false);
    const [juridictions, setJuridictions] = useState([]);
    const [conseils, setConseils] = useState([]);
    const [saving, setSaving] = useState(false);
    const [form] = Form.useForm();
    const canMark = rightsService("HSI094");

    const load = () => {
        if (!sinistreCodeId) return;
        axiosInstance.get(`/api/sinistre/contentieux/${sinistreCodeId}`)
            .then((res) => setInfo(res.data || null))
            .catch(() => { /* non marqué */ });
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [sinistreCodeId]);
    useEffect(() => {
        axiosInstance.get(`/api/parametre/type/JURIDICTION`).then((r) => setJuridictions(r.data || [])).catch(() => {});
        axiosInstance.get(`/api/conseil-juridique`)
            .then((r) => setConseils(Array.isArray(r.data) ? r.data : (r.data?.content ?? []))).catch(() => {});
    }, []);

    const openModal = () => {
        form.setFieldsValue({
            enContentieux: info ? info.enContentieux !== false : true,
            juridiction: info?.juridiction,
            conseilJuridiqueId: info?.conseilJuridiqueId,
            resumeDossier: info?.resumeDossier,
        });
        setOpen(true);
    };

    const submit = async () => {
        const v = await form.validateFields();
        setSaving(true);
        axiosInstance.put("/api/sinistre/contentieux", JSON.stringify({ sinistreCodeId, ...v }),
            { headers: { "Content-Type": "application/json" } })
            .then((res) => { setInfo(res.data); setOpen(false); message.success("Mise en contentieux enregistrée."); })
            .catch((e) => message.error(e?.response?.data?.message || "Erreur lors de l'enregistrement."))
            .finally(() => setSaving(false));
    };

    const marked = info && info.enContentieux !== false;

    return (
        <Space wrap style={{ marginBottom: 12 }}>
            {/* Aucun badge si le sinistre n'est PAS en contentieux (on n'affiche que le cas positif). */}
            {marked && (
                <Tag color="volcano" icon={<AuditOutlined />} style={{ fontSize: 13, padding: "4px 10px" }}>
                    En contentieux{info.juridiction ? ` — ${info.juridiction}` : ""}
                </Tag>
            )}
            {canMark && (
                <Button icon={<AuditOutlined />} onClick={openModal}>
                    {marked ? "Modifier le contentieux" : "Marquer en contentieux"}
                </Button>
            )}
            <Modal
                title="Mise en contentieux du sinistre"
                open={open}
                onOk={submit}
                confirmLoading={saving}
                onCancel={() => setOpen(false)}
                okText="Enregistrer"
                cancelText="Annuler"
            >
                <Form form={form} layout="vertical">
                    <Form.Item name="enContentieux" label="En contentieux" valuePropName="checked">
                        <Switch />
                    </Form.Item>
                    <Form.Item name="juridiction" label="Juridiction" rules={[{ required: true, message: "Sélectionnez la juridiction" }]}>
                        <Select showSearch optionFilterProp="label" placeholder="Sélectionner une juridiction"
                            options={juridictions.map((j) => ({ value: j.libelleParametre, label: j.libelleParametre }))} />
                    </Form.Item>
                    <Form.Item name="conseilJuridiqueId" label="Cabinet / conseil juridique affecté">
                        <Select allowClear showSearch optionFilterProp="label" placeholder="Cabinet affecté (optionnel)"
                            options={conseils.map((c) => ({ value: c.conseilJuridiqueId, label: c.nom }))} />
                    </Form.Item>
                    <Form.Item name="resumeDossier" label="Résumé du dossier">
                        <TextArea rows={3} placeholder="À renseigner par le technicien" />
                    </Form.Item>
                </Form>
            </Modal>
        </Space>
    );
};

export default ContentieuxButton;
